import React, { useState, useEffect } from 'react';
import axios from 'axios';

function MasjidList() {
  const [masjids, setMasjids] = useState([]);
  const [filteredMasjids, setFilteredMasjids] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [editingMasjid, setEditingMasjid] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    pointOfContact: '',
    contactNumber: '',
    district: '',
    image: '',
    mapUrl: ''
  });

  useEffect(() => {
    fetchMasjids();
  }, []);

  const fetchMasjids = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_REQUESTING_API}/api/masjids/`);
      setMasjids(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDistrictChange = (e) => {
    const district = e.target.value;
    setSelectedDistrict(district);
    setFilteredMasjids(
      district ? masjids.filter(m => m.district === district) : []
    );
  };

  const handleEditClick = (masjid) => {
    setEditingMasjid(masjid._id);
    setFormData(masjid);
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm('Are you sure you want to delete this masjid?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_REQUESTING_API}/api/masjids/delete/${id}`);
        fetchMasjids();
        setSelectedDistrict('');
      } catch (error) {
        console.error(error);
        alert('Error deleting masjid');
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${process.env.REACT_APP_REQUESTING_API}/api/masjids/update/${editingMasjid}`,
        formData
      );
      setEditingMasjid(null);
      fetchMasjids();
      alert('Masjid updated successfully!');
    } catch (error) {
      console.error(error);
      alert('Error updating masjid');
    }
  };

  const districts = [
    "Srikakulam","Vizianagram","Visakhapatnam","Kakinada","East Godavari",
    "Konaseema","West Godavari","Eluru","Vijayawada","Krishna","Guntur",
    "Palnadu","Bapatla","Prakasam","Nellore","Nandayala","Kurnool",
    "Anantapur","SriSathyasai","Kadapa","Annamayya","TIRUPATI","CHITTOOR"
  ];

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-2">
        Manage Masjids
      </h2>
      <p className="text-center text-gray-500 mb-10">
        Select a district to view and manage masjid data
      </p>

      {/* District Selector */}
      <div className="flex justify-center mb-10">
        <select
          value={selectedDistrict}
          onChange={handleDistrictChange}
          className="px-6 py-3 rounded-xl border border-gray-300
                     shadow-sm focus:ring-2 focus:ring-gray-400"
        >
          <option value="">Select District</option>
          {districts.map(d => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      </div>

      {/* Masjid Cards */}
      {selectedDistrict && filteredMasjids.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredMasjids.map(masjid => (
            <div
              key={masjid._id}
              className="bg-white rounded-2xl shadow-md
                         hover:shadow-xl transition-shadow
                         border overflow-hidden"
            >
              {masjid.image && (
                <img
                  src={masjid.image}
                  alt={masjid.name}
                  className="w-full h-48 object-cover"
                />
              )}

              <div className="p-5 space-y-2">
                <h3 className="text-xl font-bold text-gray-800">
                  {masjid.name}
                </h3>

                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Contact:</span>{' '}
                  {masjid.pointOfContact} ({masjid.contactNumber})
                </p>

                <p className="text-sm text-gray-600">
                  <span className="font-semibold">District:</span>{' '}
                  {masjid.district}
                </p>

                <a
                  href={masjid.mapUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block text-sm text-blue-600 hover:underline mt-1"
                >
                  View on Map
                </a>

                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => handleEditClick(masjid)}
                    className="flex-1 py-2 rounded-lg
                               bg-yellow-50 text-yellow-700
                               border border-yellow-200
                               hover:bg-yellow-100"
                  >
                    Edit
                  </button>

                  <button
                    onClick={() => handleDeleteClick(masjid._id)}
                    className="flex-1 py-2 rounded-lg
                               bg-red-50 text-red-600
                               border border-red-200
                               hover:bg-red-100"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedDistrict && filteredMasjids.length === 0 && (
        <p className="text-center text-gray-500">
          No masjids found in {selectedDistrict}.
        </p>
      )}

      {/* Edit Form */}
      {editingMasjid && (
        <div className="mt-14 bg-white rounded-2xl shadow-lg border p-6">
          <h3 className="text-2xl font-semibold text-gray-800 mb-6">
            Edit Masjid
          </h3>

          <form onSubmit={handleUpdateSubmit} className="space-y-4">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Masjid Name"
              required
              className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-300"
            />

            <input
              type="text"
              name="pointOfContact"
              value={formData.pointOfContact}
              onChange={handleChange}
              placeholder="Point of Contact"
              required
              className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-300"
            />

            <input
              type="text"
              name="contactNumber"
              value={formData.contactNumber}
              onChange={handleChange}
              placeholder="Contact Number"
              required
              className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-300"
            />

            <select
              name="district"
              value={formData.district}
              onChange={handleChange}
              required
              className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-300"
            >
              <option value="">Select District</option>
              {districts.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>

            <input
              type="file"
              onChange={handleFileChange}
              accept="image/*"
              className="w-full text-sm"
            />

            <input
              type="text"
              name="mapUrl"
              value={formData.mapUrl}
              onChange={handleChange}
              placeholder="Google Maps URL"
              required
              className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-300"
            />

            <div className="flex gap-4 pt-2">
              <button
                type="submit"
                className="px-6 py-2.5 rounded-lg
                           bg-green-50 text-green-700
                           border border-green-200
                           hover:bg-green-100"
              >
                Update Masjid
              </button>

              <button
                type="button"
                onClick={() => setEditingMasjid(null)}
                className="px-6 py-2.5 rounded-lg
                           bg-gray-100 text-gray-700
                           border border-gray-300
                           hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default MasjidList;



// working 16/12/25

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';

// function MasjidList() {
//   const [masjids, setMasjids] = useState([]);
//   const [filteredMasjids, setFilteredMasjids] = useState([]);
//   const [selectedDistrict, setSelectedDistrict] = useState('');
//   const [editingMasjid, setEditingMasjid] = useState(null);
//   const [formData, setFormData] = useState({
//     name: '',
//     pointOfContact: '',
//     contactNumber: '',
//     district: '',
//     image: '',
//     mapUrl: ''
//   });

//   useEffect(() => {
//     fetchMasjids();
//   }, []);

//   const fetchMasjids = async () => {
//   try {
//     const res = await axios.get(`${process.env.REACT_APP_REQUESTING_API}/api/masjids/`);
//     setMasjids(res.data);
//   } catch (error) {
//     console.error(error);
//   }
// };


//   const handleDistrictChange = (e) => {
//     const district = e.target.value;
//     setSelectedDistrict(district);

//     if (district) {
//       const filtered = masjids.filter(masjid => masjid.district === district);
//       setFilteredMasjids(filtered);
//     } else {
//       setFilteredMasjids([]);
//     }
//   };

//   const handleEditClick = (masjid) => {
//     setEditingMasjid(masjid._id);
//     setFormData(masjid);
//   };

//  const handleDeleteClick = async (id) => {
//   if (window.confirm('Are you sure you want to delete this masjid?')) {
//     try {
//       await axios.delete(`${process.env.REACT_APP_REQUESTING_API}/api/masjids/delete/${id}`);
//       fetchMasjids();
//       setSelectedDistrict(''); // Reset view after delete
//     } catch (error) {
//       console.error(error);
//       alert('Error deleting masjid');
//     }
//   }
// };


//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData({ ...formData, [name]: value });
//   };

//   const handleFileChange = (e) => {
//     const file = e.target.files[0];
//     if (file) {
//       const reader = new FileReader();
//       reader.onloadend = () => {
//         setFormData({ ...formData, image: reader.result });
//       };
//       reader.readAsDataURL(file);
//     }
//   };

//  const handleUpdateSubmit = async (e) => {
//   e.preventDefault();
//   try {
//     await axios.put(`${process.env.REACT_APP_REQUESTING_API}/api/masjids/update/${editingMasjid}`, formData);
//     setEditingMasjid(null);
//     fetchMasjids();
//     alert('Masjid updated successfully!');
//   } catch (error) {
//     console.error(error);
//     alert('Error updating masjid');
//   }
// };


//   const districts = [
//     "Srikakulam", "Vizianagram", "Visakhapatnam", "Kakinada",
//     "East Godavari", "Konaseema", "West Godavari", "Eluru", "Vijayawada",
//     "Krishna", "Guntur", "Palnadu", "Bapatla", "Prakasam", "Nellore",
//     "Nandayala", "Kurnool", "Anantapur", "SriSathyasai", "Kadapa",
//     "Annamayya", "TIRUPATI", "CHITTOOR"
//   ];

//   return (
//     <div className="max-w-7xl mx-auto p-6 ">
//       <h2 className="text-4xl font-bold text-center mb-8">Manage Masjids</h2>
//       <h2 className="text-xl  text-center mb-8">Select District to get Masjid Data</h2>


//       {/* District Dropdown */}
//       <div className="mb-8 flex justify-center">
//         <select
//           value={selectedDistrict}
//           onChange={handleDistrictChange}
//           className="px-6 py-3 border rounded-md shadow-md"
//         >
//           <option value="">Select District</option>
//           {districts.map((district) => (
//             <option key={district} value={district}>
//               {district}
//             </option>
//           ))}
//         </select>
//       </div>

//       {/* Cards */}
//       {selectedDistrict && filteredMasjids.length > 0 ? (
//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
//           {filteredMasjids.map((masjid) => (
//             <div key={masjid._id} className="border rounded-lg shadow-lg overflow-hidden">
//               {masjid.image && (
//                 <img
//                   src={masjid.image}
//                   alt={masjid.name}
//                   className="h-48 w-full object-cover"
//                 />
//               )}
//               <div className="p-4">
//                 <h3 className="text-xl font-bold mb-2">{masjid.name}</h3>
//                 <p className="text-gray-700 mb-1">Contact: {masjid.pointOfContact} ({masjid.contactNumber})</p>
//                 <p className="text-gray-600 mb-2">District: {masjid.district}</p>
//                 <a
//                   href={masjid.mapUrl}
//                   target="_blank"
//                   rel="noreferrer"
//                   className="text-blue-500 underline mb-3 block"
//                 >
//                   View on Map
//                 </a>
//                 <div className="flex gap-2">
//                   <button
//                     className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
//                     onClick={() => handleEditClick(masjid)}
//                   >
//                     Edit
//                   </button>
//                   <button
//                     className="flex-1 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
//                     onClick={() => handleDeleteClick(masjid._id)}
//                   >
//                     Delete
//                   </button>
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>
//       ) : selectedDistrict ? (
//         <p className="text-center text-gray-500">No Masjids found in {selectedDistrict}.</p>
//       ) : null}

//       {/* Edit Form */}
//       {editingMasjid && (
//         <div className="mt-10 p-6 border rounded-md shadow-md bg-gray-50">
//           <h3 className="text-2xl font-semibold mb-4">Edit Masjid</h3>
//           <form onSubmit={handleUpdateSubmit} className="space-y-4">
//             <input
//               type="text"
//               name="name"
//               value={formData.name}
//               onChange={handleChange}
//               placeholder="Masjid Name"
//               required
//               className="w-full px-4 py-2 border rounded-md"
//             />
//             <input
//               type="text"
//               name="pointOfContact"
//               value={formData.pointOfContact}
//               onChange={handleChange}
//               placeholder="Point of Contact"
//               required
//               className="w-full px-4 py-2 border rounded-md"
//             />
//             <input
//               type="text"
//               name="contactNumber"
//               value={formData.contactNumber}
//               onChange={handleChange}
//               placeholder="Contact Number"
//               required
//               className="w-full px-4 py-2 border rounded-md"
//             />
//             <select
//               name="district"
//               value={formData.district}
//               onChange={handleChange}
//               required
//               className="w-full px-4 py-2 border rounded-md"
//             >
//               <option value="">Select District</option>
//               {districts.map(district => (
//                 <option key={district} value={district}>{district}</option>
//               ))}
//             </select>
//             <input
//               type="file"
//               onChange={handleFileChange}
//               accept="image/*"
//               className="block w-full"
//             />
//             <input
//               type="text"
//               name="mapUrl"
//               value={formData.mapUrl}
//               onChange={handleChange}
//               placeholder="Google Maps URL"
//               required
//               className="w-full px-4 py-2 border rounded-md"
//             />
//             <div className="flex gap-4">
//               <button
//                 type="submit"
//                 className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
//               >
//                 Update Masjid
//               </button>
//               <button
//                 type="button"
//                 onClick={() => setEditingMasjid(null)}
//                 className="px-6 py-2 bg-gray-400 text-white rounded-md hover:bg-gray-500"
//               >
//                 Cancel
//               </button>
//             </div>
//           </form>
//         </div>
//       )}
//     </div>
//   );
// };

// export default MasjidList
